import { CommonUnixMsEnum } from "../enums"
import {
  bundleErrorLogs,
  fetchSourceFiles,
  triggerSync,
  fetchIntegrationWarnings,
  markIntegrationWarningResolved,
  publishToModernApi,
} from "../providers"
import { DocDownloadQueueItem } from "../screens/DocExports/DocExport"
import { AssociationId, AuthCredentials, FileConfig, IntegrationNamesEnum, ProfilePk, UnixMsString } from "../types"
import { toUpperCaseCustom, toValidPfDate, writeRemoteFilesToLocalStorage } from "../utils"
import { SessionService } from "./sessionService"

class IntegrationsService {
  async triggerSync(
    integrationName: IntegrationNamesEnum,
    associationId: AssociationId,
    authCredentials: AuthCredentials,
    shouldPostBatchProfiles?: boolean,
  ) {
    const { username, password } = authCredentials
    const token = await SessionService.prepareAuthTokens(username, password)
    return await triggerSync(integrationName, associationId, token, shouldPostBatchProfiles)
  }

  async bundleErrorLogs(
    associationId: AssociationId,
    integrationName: IntegrationNamesEnum,
    authCredentials: AuthCredentials,
  ) {
    const { username, password } = authCredentials
    const token = await SessionService.prepareAuthTokens(username, password)
    const fromUnixMsString = `${Date.now() - CommonUnixMsEnum.ONE_WEEK}` as UnixMsString
    const toUnixMsString = `${Date.now()}` as UnixMsString
    return await bundleErrorLogs(associationId, integrationName, { fromUnixMsString, toUnixMsString }, token)
  }

  async downloadErrorLogsFile(fileConfig: FileConfig) {
    const { filePath = "", fileName = "", fileFormat = "" } = fileConfig
    const fileNameAndFormat = `${fileName}.${fileFormat}`
    const fullFilePath = `${filePath}/${fileNameAndFormat}`
    const docsToDownload = [{ docPath: fullFilePath, filename: fileNameAndFormat }]
    return await writeRemoteFilesToLocalStorage(docsToDownload)
  }

  async fetchSourceFiles(
    integrationName: IntegrationNamesEnum,
    associationId: AssociationId,
    authCredentials: AuthCredentials,
  ) {
    const { username, password } = authCredentials
    const token = await SessionService.prepareAuthTokens(username, password)
    const sourceFileMetaData = await fetchSourceFiles(integrationName, associationId, token)
    const sourceFiles = sourceFileMetaData.map(({ Key }) => {
      const ketElement = Key.split("/")
      const fileName = ketElement[ketElement.length - 1]
      const timestamp = toValidPfDate(ketElement[ketElement.length - 2], "YYYY-MM-DD, HH:mm")
      const label = `${timestamp} \xa0 | \xa0 ${toUpperCaseCustom(fileName)}`
      const queueId = Key
      const status = ""
      const fileLocation = { key: Key }
      return { label, queueId, status, fileLocation } as DocDownloadQueueItem
    })
    return sourceFiles
  }

  async fetchIntegrationWarnings(association: AssociationId, authCredentials: AuthCredentials) {
    const { username, password } = authCredentials
    const token = await SessionService.prepareAuthTokens(username, password)
    const integrationWarnings = await fetchIntegrationWarnings(association, token)
    return integrationWarnings
  }

  async markIntegrationWarningResolved(
    selectedProfileIds: ProfilePk[],
    association: AssociationId,
    authCredentials: AuthCredentials,
  ) {
    const { username, password } = authCredentials
    const token = await SessionService.prepareAuthTokens(username, password)
    const integrationWarnings = await markIntegrationWarningResolved(selectedProfileIds, association, token)
    return integrationWarnings
  }

  async publishToModernApi(associationId: AssociationId, authCredentials: AuthCredentials) {
    const { username, password } = authCredentials
    const token = await SessionService.prepareAuthTokens(username, password)
    return await publishToModernApi(associationId, token)
  }
}

export { IntegrationsService }
